import type { SharedProps } from '@bonniernews/dn-design-system-web/assets/types/shared-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { IconUse } from '@bonniernews/dn-design-system-web/components/icon-sprite/icon-sprite.tsx'

interface PillBaseProps extends SharedProps {
  text: string
  size?: 'sm' | 'lg'
  selected?: boolean
}

export interface PillButtonProps extends PillBaseProps {
  onClick?: () => void
}

export type PillProps =
  | ({
      variant?: 'link'
      href: string
      showIcon?: boolean
    } & PillBaseProps)
  | ({ variant: 'button' } & PillButtonProps)

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/pill](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/pill)
 * - Storybook: [Pill](https://designsystem.dn.se/?path=/docs/basic-pill--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/pill/pill.scss'`
 *
 * Two variants available:
 * - `link` — used for navigation, supports `selected` state and has an optional arrow icon
 * - `button` — used as a toggleable button, supports `selected` state
 */
export const Pill = ({
  text,
  variant = 'link',
  size = 'lg',
  classNames,
  attributes = {},
  selected = false,
  ...rest
}: PillProps) => {
  const classNamePrefix = 'ds-pill--'
  const classes = formatClassString([
    'ds-pill',
    `${classNamePrefix}${variant}`,
    `${classNamePrefix}${size}`,
    selected && `${classNamePrefix}selected`,
    classNames,
  ])

  if (variant === 'button') {
    const { onClick } = rest as PillButtonProps
    return (
      <button type='button' className={classes} onClick={onClick} aria-pressed={selected} {...attributes}>
        <span>{text}</span>
      </button>
    )
  }

  const { href, showIcon = true } = rest as { href: string; showIcon?: boolean }
  return (
    <a href={href} className={classes} aria-current={selected ? 'page' : undefined} {...attributes}>
      <span>{text}</span>
      {showIcon && <IconUse {...{ iconName: 'arrow_forward', attributes: { 'aria-hidden': 'true' } }} />}
    </a>
  )
}
